import "./TreeProgressBanner.css";
import {useParams} from "react-router-dom";
import {useQuery} from "@tanstack/react-query";
import {getJob} from "../../api/Client";

interface TreeProgressBannerProps {
    /** Which chapter is currently showing in the tree — comes from the URL
     *  (see Learn.tsx's chapterIndex), so the banner always matches whatever
     *  chapter is actually on screen instead of always chapter 0. */
    chapterIndex: number;
    /** Show a small X to let people hide the banner */
    onDismiss?: () => void;
    className?: string;
}

// Walks every node group's nodes and counts which ones have actually been
// answered — the same "answered" check used to flatten the tree
// (node.answerGrade !== null) — rather than trusting a separate,
// easily-stale aggregate off the subject.
function countProblems(nodeGroups: any[] | undefined): { solved: number; total: number } {
    const nodes = (nodeGroups ?? []).flatMap((group: any) => group.nodes ?? []);
    return {
        total: nodes.length,
        solved: nodes.filter((node: any) => node.answerGrade !== null && node.answerGrade !== undefined).length,
    };
}

export default function TreeProgressBanner({
                                               chapterIndex,
                                               onDismiss,
                                               className = "",
                                           }: TreeProgressBannerProps) {

    const { jobId } = useParams<{ jobId: string }>();

    const {data, isLoading} = useQuery({
        queryKey: ["job", jobId],
        queryFn: () => getJob(jobId!),
        enabled: !!jobId, // Don't run until jobId exists
    });

    const currentChapter = data?.subject?.chapters?.[chapterIndex];

    const { solved, total } = countProblems(currentChapter?.nodeGroups);
    const percent = total > 0 ? Math.round((solved / total) * 100) : 0;

    return (
        <div className={`tree-banner ${className}`.trim()} role="status">
            {onDismiss && (
                <button className="tree-banner__dismiss" onClick={onDismiss} aria-label="Dismiss">
                    <svg viewBox="0 0 24 24" width="12" height="12" aria-hidden="true">
                        <path d="M6 6 L18 18 M18 6 L6 18" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" />
                    </svg>
                </button>
            )}

            <div className="tree-banner__heading">
                <span className="eyebrow tree-banner__eyebrow">{currentChapter?.title}</span>
            </div>

            <h3 className="tree-banner__title">{data?.subject?.title || ""}</h3>

            <div className="tree-banner__progress">
                <div
                    className="tree-banner__track"
                    role="progressbar"
                    aria-valuenow={percent}
                    aria-valuemin={0}
                    aria-valuemax={100}
                    aria-label={`${data?.subject?.title || ""} progress`}
                >
                    <div className="tree-banner__fill" style={{ width: `${percent}%` }} />
                </div>
                <span className="tree-banner__percent">{percent}%</span>
            </div>

            <p className="tree-banner__stat">
                {solved} of {total} problems solved
            </p>
        </div>
    );
}